import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { checkSubdomain } from "../../lib/api/cloud";

interface SubdomainInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidation: (valid: boolean) => void;
}

const DOMAIN_SUFFIX = ".cc.somebugs.com";

export function SubdomainInput({ value, onChange, onValidation }: SubdomainInputProps) {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valid, setValid] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!value) {
      setError(null);
      setValid(false);
      onValidation(false);
      return;
    }

    // Client-side validation first
    if (value.length < 3) {
      setError(t('dashboard.subdomain.minLength'));
      setValid(false);
      onValidation(false);
      return;
    }
    if (value.length > 20) {
      setError(t('dashboard.subdomain.maxLength'));
      setValid(false);
      onValidation(false);
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(value)) {
      setError(t('dashboard.subdomain.invalidFormat'));
      setValid(false);
      onValidation(false);
      return;
    }

    // Debounced server-side check
    setChecking(true);
    setError(null);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        const result = await checkSubdomain(value);
        if (result.available) {
          setValid(true);
          setError(null);
          onValidation(true);
        } else {
          setValid(false);
          setError(result.reason ?? t('dashboard.subdomain.notAvailable'));
          onValidation(false);
        }
      } catch {
        setError(t('dashboard.subdomain.checkFailed'));
        setValid(false);
        onValidation(false);
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value, onValidation, t]);

  return (
    <div>
      <label className="block text-[#f5f5f5] text-sm font-medium mb-1.5">{t('dashboard.subdomain.label')}</label>
      <div className="flex items-center">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          placeholder={t('dashboard.subdomain.placeholder')}
          className={`flex-1 px-3 py-2.5 bg-[#111] border rounded-l-lg text-[#f5f5f5] text-sm
                     placeholder-[#555] focus:outline-none focus:ring-2 focus:ring-blue-500/50
                     ${
                       error ? "border-red-500/50" : valid ? "border-green-500/50" : "border-[#333]"
                     }`}
          maxLength={20}
        />
        <span className="px-3 py-2.5 bg-[#1a1a1a] border border-l-0 border-[#333] rounded-r-lg text-[#666] text-sm">
          {DOMAIN_SUFFIX}
        </span>
      </div>

      <div className="mt-1.5 h-5">
        {checking && <span className="text-[#666] text-xs">{t('dashboard.subdomain.checking')}</span>}
        {!checking && error && <span className="text-red-400 text-xs">{error}</span>}
        {!checking && valid && (
          <span className="text-green-400 text-xs">
            {t('dashboard.subdomain.available', { subdomain: value })}
          </span>
        )}
      </div>

      {value && valid && (
        <p className="text-[#999] text-xs mt-1">
          {t('dashboard.subdomain.yourUrl')}{" "}
          <span className="text-[#f5f5f5] font-medium">
            {value}
            {DOMAIN_SUFFIX}
          </span>
        </p>
      )}
    </div>
  );
}
